import type { ProductConfig } from './types'

/**
 * Een product met alleen een draaitafel: de camera- en materiaalvelden
 * gelden pas als er een GLB bijkomt, en staan dan op dezelfde standaard als
 * product-01.
 */
function metDraaitafel(
  id: string, naam: string, sheet: string, omschrijving: ProductConfig['omschrijving'], frames: number[], start: number,
): ProductConfig {
  const map = `/products/${id}/draai/`
  return {
    id, naam, sheet, omschrijving,
    modelUrl: null,
    previewUrl: `${map}${String(start).padStart(2, '0')}.webp`,
    draaitafel: { map, frames, start },
    schaal: 1,
    rotatie: [0, 0, 0],
    camera: { richting: [0.78, 0.42, 1], afstand: 1.75 },
    kijkhoogte: 0.42,
    belichting: 1.05,
    omgevingsSterkte: 1,
    autoDraaien: true,
  }
}

const ALLE_36 = Array.from({ length: 36 }, (_, i) => i)

/**
 * De tien producten. Product-01 tot en met 05 zijn ingevuld; de overige staan
 * als lege plek klaar zodat de volgorde en de mappenstructuur al vastliggen.
 *
 * `modelUrl: null` betekent: er is nog geen echte GLB. Heeft het product een
 * draaitafel (de renders van Linde, 36 om de 10°), dan draait de viewer die
 * rond; anders toont hij de preview met een melding. Hij bouwt bewust géén
 * benadering uit primitieven — dat is precies wat een productviewer niet moet
 * doen. Zodra het GLB er is, verandert er niets anders dan die ene regel.
 */
export const PRODUCTEN: ProductConfig[] = [
  {
    id: 'product-01',
    naam: 'Linde T-MATIC',
    sheet: 't-matic',
    omschrijving: {
      nl: 'Autonome pallettruck voor horizontaal transport tot 3.000 kg',
      en: 'Automated pallet truck for horizontal transport up to 3,000 kg',
    },
    modelUrl: null, // → public/products/product-01/model.glb
    previewUrl: '/products/product-01/draai/16.webp',
    // De draaitafel van de T-MATIC core op linde-mh.com (22 oktober; de oude
    // pagina /T-MATIC/ had er geen, en tot dan bladerde de viewer door vier
    // fabrieksfoto's). De originelen van 1200 px, zie docs/linde-draaitafels.md.
    // Frame 16: de bedienzijde met het logo, en de vorken.
    draaitafel: { map: '/products/product-01/draai/', frames: ALLE_36, start: 16 },
    schaal: 1,
    rotatie: [0, 0, 0],
    // Driekwart van voren, zoals een productfoto: je ziet de vorken én de
    // bedienzijde. Iets boven ooghoogte, want vanaf de vloer wordt het een
    // heldenshot en dat leest als reclame in plaats van als product.
    camera: { richting: [0.78, 0.42, 1], afstand: 1.75 },
    kijkhoogte: 0.42,
    belichting: 1.05,
    omgevingsSterkte: 1,
    autoDraaien: true,
    materialen: [
      { namen: ['band', 'rubber', 'Tire', 'Rubber'], ruwheid: 0.82, metaal: 0, omgeving: 0.35 },
      { namen: ['lak', 'rood', 'Paint', 'Body'], ruwheid: 0.34, metaal: 0.02, lak: 1, lakRuwheid: 0.07 },
      { namen: ['kunststof', 'Plastic'], ruwheid: 0.55, metaal: 0.04, lak: 0.3, lakRuwheid: 0.3 },
      { namen: ['staal', 'Metal', 'Steel'], ruwheid: 0.38, metaal: 0.9, omgeving: 1.1 },
      { namen: ['glas', 'Glass'], ruwheid: 0.05, metaal: 0, omgeving: 1.4 },
    ],
    referenties: [
      '/products/product-01/ref/zij.png',
      '/products/product-01/ref/achter.png',
      '/products/product-01/ref/driekwart.png',
      '/products/product-01/ref/voor.png',
    ],
  },
  // De Matics met een draaitafel op linde-mh.com. De K-MATIC mist er twee (2
  // en 34); de viewer slaat die over. Teksten zoals in de hotspots
  // (`content.ts`), op het typeblad nagelopen.
  metDraaitafel('product-02', 'Linde K-MATIC', 'k-matic', {
    nl: 'Autonome smallegangentruck met draaikop, tot 14 m hoog',
    en: 'Automated very narrow aisle truck with turret head, up to 14 m',
  }, ALLE_36.filter((i) => i !== 2 && i !== 34), 9),
  metDraaitafel('product-03', 'Linde R-MATIC', 'r-matic', {
    nl: 'Autonome reachtruck voor pallets tot 1.600 kg, tot 11 m hoog',
    en: 'Automated reach truck for pallets up to 1,600 kg, up to 11 m',
  }, ALLE_36, 9),
  metDraaitafel('product-04', 'Linde L-MATIC core', 'l-matic', {
    nl: 'Compacte autonome palletstapelaar voor productiebevoorrading',
    en: 'Compact automated pallet stacker for production supply',
  }, ALLE_36, 0),
  metDraaitafel('product-05', 'Linde C-MATIC', 'c-matic', {
    nl: 'Plat autonoom voertuig dat onder de lading rijdt en hem wegbrengt',
    en: 'Low automated vehicle that drives under the load and carries it away',
  }, ALLE_36, 0),
]

/** Plaatsen 6 t/m 10 — nog niet aangeleverd, wel al genummerd. */
export const GEPLAND = Array.from({ length: 5 }, (_, i) => {
  const nr = String(i + 6).padStart(2, '0')
  return { id: `product-${nr}`, map: `/public/products/product-${nr}/` }
})

export const vindProduct = (id: string | null | undefined): ProductConfig | undefined =>
  PRODUCTEN.find((p) => p.id === id)

/** Product bij een Matic-model uit de magazijnscène, zodat de hotspot erop kan linken. */
export const productBijModel = (model: string): ProductConfig | undefined =>
  PRODUCTEN.find((p) => p.naam.toLowerCase().includes(model.toLowerCase()))
